/**
 * taxBundleCompare — เทียบไฟล์ export (bundle .json) ของ Tax Planning V2 สองไฟล์เข้าด้วยกัน
 * โดยไม่ต้องมี database: ทุกอย่างอ่านจากตัวไฟล์ที่ผู้ใช้วาง/อัปโหลดเข้ามาเท่านั้น.
 *
 * ทำไมถึง reuse ตัว diff ของ save-history ได้ตรง ๆ:
 *   ไฟล์ `lhb.taxquestion.v2.bundle` เก็บ `steps[]` / `items[]` ไว้ที่ top-level ของ envelope —
 *   ซึ่งเป็นรูปเดียวกับ `payloadJson` ของ snapshot พอดี เช่นเดียวกับ `lhb.taxcalc.v2.bundle`
 *   ที่เก็บ `functions[]` ที่ top-level. เพราะฉะนั้น `diffSnapshots()` ของทั้งสองสไลซ์กินตัว
 *   bundle ทั้งก้อนได้เลย (คีย์ที่มันไม่รู้จัก — `kind` / `source` / … — ไม่ใช่ array ของ record
 *   จึงถูกมองข้ามโดย `parseSnapshotPayload` อยู่แล้ว) และ "ไม่มี hard-coded column list" ของ
 *   ตัว diff เดิมก็ยังเป็นจริงต่อที่นี่: คอลัมน์ที่ bundle เพิ่มวันหลังโผล่ในหน้าเทียบเอง.
 *
 * สองเรื่องที่ไฟล์นี้ต้องทำเพิ่มจาก diff เดิม:
 *
 *   1. 🔴 `source` เป็น PROVENANCE ไม่ใช่เนื้อหา — `taxYearCode` / `beYear` / `configVersionCode` /
 *      `questionVersionCode` / `calcVersionCode` ต่างกันเป็นเรื่องปกติ (คนละเครื่อง คนละ version)
 *      ถ้านับเป็น "แก้" ทุกครั้งที่เทียบข้ามเครื่อง หน้าจอจะเตือนสิ่งที่ไม่ใช่การเปลี่ยนแปลง
 *      จึงถูกแยกไปแสดงเป็น "ที่มาของไฟล์" และไม่เข้า summary. ส่วนที่เป็นเนื้อหาจริงของ header —
 *      `description` และ (ฝั่งคำถาม) `resumePromptTh` ที่ import คัดลอกลงเวอร์ชันใหม่ — ยังถูกเทียบ
 *      ในกลุ่ม header ตามปกติ ไม่งั้นการแก้ prompt ของทั้งชุดจะหายไปจากผลเทียบ.
 *
 *   2. ผลลัพธ์ของทั้งสอง kind ถูก normalize ให้เป็นรูปเดียว (`groups[] → rows[] → fields[]`) พร้อม
 *      `chips` ที่คำนวณไว้แล้ว เพื่อให้ dialog ตัวเดียวเรนเดอร์ได้ทุกชนิดโดยไม่ต้องรู้ว่าเป็นชุดคำถาม
 *      หรือชุดสูตร.
 *
 * Pure logic — ไม่ import Vue/Vuetify — จึงรันด้วย node ได้ตรง ๆ ในเทสต์
 *   Test:  cd WebAPI/ClientApp && node --test tests/taxBundleCompare.test.mjs
 */

import { diffSnapshots as diffQuestionSnapshots } from './taxQuestionSnapshotDiff.js';
import { diffSnapshots as diffCalcSnapshots } from './taxCalcSnapshotDiff.js';

/** kind ที่หน้าเทียบรองรับ — ต้องตรงกับ `BundleKind` ของ DTO ฝั่ง C# ตัวต่อตัว. */
export const BUNDLE_KINDS = Object.freeze({
  question: 'lhb.taxquestion.v2.bundle',
  calc: 'lhb.taxcalc.v2.bundle',
});

/** kind ที่ "รู้จักแต่ยังไม่รองรับ" — บอกผู้ใช้ตรง ๆ ดีกว่าปล่อยให้เห็น "ไม่ใช่ไฟล์ export". */
const KNOWN_OTHER_KINDS = Object.freeze({
  'lhb.taxconfig.v2.bundle': 'ไฟล์เวอร์ชันคอนฟิกทั้งก้อน (Tax Config V2)',
  'lhb.taxsettings.v2.bundle': 'ไฟล์การตั้งค่ากลาง (Tax Planning Settings V2)',
  'lhb.taxconfig.bundle': 'ไฟล์คอนฟิกรุ่นเก่า (V1)',
});

export const KIND_LABEL = Object.freeze({
  question: 'ชุดคำถาม (Tax Question V2)',
  calc: 'ชุดสูตรคำนวณ (Tax Calculate V2)',
});

/**
 * ฟิลด์ของ `source` ที่เป็น provenance ล้วน ๆ — แสดงเป็นที่มาของไฟล์ ไม่นับเป็นการเปลี่ยนแปลง
 * (ดูเหตุผลข้อ 1 ในหัวไฟล์). เทียบแบบ case-insensitive เหมือนตัว diff เดิม.
 */
const PROVENANCE_FIELDS = Object.freeze([
  'taxYearCode',
  'beYear',
  'configVersionCode',
  'questionVersionCode',
  'calcVersionCode',
]);

const PROVENANCE = new Set(PROVENANCE_FIELDS.map((f) => f.toLowerCase()));

/** อ่าน property แบบไม่สนตัวพิมพ์ — ตรงตัวก่อน (ทางปกติ) แล้วค่อยไล่หา. */
function getProp(obj, name) {
  if (!obj || typeof obj !== 'object') return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, name)) return obj[name];
  const lname = String(name).toLowerCase();
  const hit = Object.keys(obj).find((k) => k.toLowerCase() === lname);
  return hit === undefined ? undefined : obj[hit];
}

/** null/undefined → '' · object/array → JSON · อื่น ๆ → String(). */
function normalizeValue(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
}

/** นับแถวจริงในลิสต์ (ค่าที่ไม่ใช่ record ไม่ถูกนับ — ให้ตรงกับที่ตัว diff มองเห็น). */
function countRows(bundle, name) {
  const value = getProp(bundle, name);
  if (!Array.isArray(value)) return 0;
  return value.filter((r) => r && typeof r === 'object' && !Array.isArray(r)).length;
}

/**
 * ข้อความ → `{ ok, kind, bundle, header, error }`.
 *
 * ไฟล์ที่ผู้ใช้ลากเข้ามาคือ input ที่ไม่น่าเชื่อถือ — ทุกทางที่พังต้องได้ข้อความไทยที่บอกว่าไฟล์
 * เป็นอะไรกันแน่ ไม่ใช่ throw กลางหน้าจอ.
 */
export function parseBundleText(text, { fileName = '' } = {}) {
  const raw = String(text || '').trim();
  if (!raw) return { ok: false, error: 'ยังไม่ได้ใส่ JSON' };

  let bundle;
  try {
    bundle = JSON.parse(raw);
  } catch (e) {
    return { ok: false, error: `อ่าน JSON ไม่ออก — ${(e && e.message) || 'รูปแบบไม่ถูกต้อง'}` };
  }
  if (!bundle || typeof bundle !== 'object' || Array.isArray(bundle)) {
    return { ok: false, error: 'ไฟล์นี้ไม่ใช่ไฟล์ export ของ Tax Planning V2 — เนื้อไฟล์ไม่ใช่ object' };
  }

  const kindRaw = normalizeValue(getProp(bundle, 'kind')).trim();
  const kindLower = kindRaw.toLowerCase();
  const kind = Object.keys(BUNDLE_KINDS).find((k) => BUNDLE_KINDS[k] === kindLower);

  if (!kind) {
    if (KNOWN_OTHER_KINDS[kindLower]) {
      return {
        ok: false,
        error: `ยังเทียบ ${KNOWN_OTHER_KINDS[kindLower]} ไม่ได้ — หน้านี้รองรับเฉพาะไฟล์ชุดคำถามและชุดสูตรคำนวณ`,
      };
    }
    return {
      ok: false,
      error: kindRaw
        ? `ไฟล์นี้ไม่ใช่ไฟล์ export ของ Tax Planning V2 — kind ในไฟล์คือ "${kindRaw}"`
        : 'ไฟล์นี้ไม่ใช่ไฟล์ export ของ Tax Planning V2 — ไม่พบ kind ในไฟล์',
    };
  }

  return { ok: true, kind, bundle, header: bundleHeader(bundle, kind, fileName) };
}

/**
 * หัวไฟล์ที่เอาไว้โชว์เหนือช่องแต่ละฝั่ง: มาจากไหน · export เมื่อไร · มีของกี่แถว.
 * ทุกฟิลด์ fail-soft — ไฟล์ที่ export ตอนหา parent row ไม่เจอมี `source` ว่างได้ตามสเปกฝั่ง C#.
 */
export function bundleHeader(bundle, kind, fileName = '') {
  const source = getProp(bundle, 'source') || {};
  const versionCode = normalizeValue(
    kind === 'calc' ? getProp(source, 'calcVersionCode') : getProp(source, 'questionVersionCode'),
  );
  const counts = kind === 'calc'
    ? [{ label: 'สูตร', value: countRows(bundle, 'functions') }]
    : [
      { label: 'Section', value: countRows(bundle, 'steps') },
      { label: 'คำถาม', value: countRows(bundle, 'items') },
    ];

  return {
    kind,
    kindLabel: KIND_LABEL[kind] || kind,
    fileName: fileName || '',
    schemaVersion: normalizeValue(getProp(bundle, 'schemaVersion')),
    exportedAt: normalizeValue(getProp(bundle, 'exportedAt')),
    exportedBy: normalizeValue(getProp(bundle, 'exportedBy')),
    versionCode,
    taxYearCode: normalizeValue(getProp(source, 'taxYearCode')),
    beYear: normalizeValue(getProp(source, 'beYear')),
    configVersionCode: normalizeValue(getProp(source, 'configVersionCode')),
    description: normalizeValue(getProp(source, 'description')),
    counts,
    /** ป้ายสั้น ๆ ที่ใช้เป็นหัวคอลัมน์ของตารางเทียบ — รหัสเวอร์ชันถ้ามี ไม่งั้นชื่อไฟล์ */
    label: versionCode || fileName || '',
  };
}

/**
 * ฟิลด์ header ที่เป็นเนื้อหาจริง (ไม่ใช่ provenance) — union ของสองฝั่ง เหมือนที่ตัว diff เดิมทำกับ
 * แถวข้อมูล เพื่อไม่ให้ต้องมีลิสต์คอลัมน์ตายตัวที่นี่.
 */
function headerRows(leftSource, rightSource) {
  const seen = new Map();
  [rightSource, leftSource].forEach((src) => {
    if (!src || typeof src !== 'object') return;
    Object.keys(src).forEach((k) => {
      const lk = k.toLowerCase();
      if (PROVENANCE.has(lk) || seen.has(lk)) return;
      seen.set(lk, k);
    });
  });

  const fields = [];
  seen.forEach((display, lk) => {
    const lv = normalizeValue(getProp(leftSource, lk));
    const rv = normalizeValue(getProp(rightSource, lk));
    if (lv !== rv) fields.push({ name: display, left: lv, right: rv });
  });

  if (!fields.length) return [];
  return [{
    key: 'header',
    state: 'modified',
    label: 'ข้อมูลหัวชุด (header)',
    chips: [],
    fields,
  }];
}

/** chip ที่โชว์ข้าง ๆ ชื่อแถว — คำนวณที่นี่ dialog จะได้ไม่ต้องรู้ว่าเป็น kind ไหน. */
function chipsOf(row) {
  return [row.itemKind, row.stepCode, row.functionCode, row.fieldKey]
    .map((v) => normalizeValue(v))
    .filter((v, i, arr) => v && arr.indexOf(v) === i);
}

function decorate(rows) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({ ...row, chips: chipsOf(row) }));
}

/**
 * เทียบ bundle สองไฟล์ (ซ้าย = ไฟล์ที่ถือว่าเก่ากว่า).
 *
 * → `{ ok, kind, kindLabel, summary: { added, removed, modified, unchanged },
 *      groups: [ { name, title, testid, rows } ] }`
 *   หรือ `{ ok: false, error }` เมื่อสอง kind ไม่เหมือนกัน (เทียบชุดคำถามกับชุดสูตรไม่มีความหมาย).
 *
 * `summary` นับเฉพาะแถวข้อมูล — แถว header ไม่ถูกนับ เพราะมันเป็นแถวสังเคราะห์แถวเดียวที่โผล่มา
 * ก็ต่อเมื่อมีอะไรต่าง ถ้านับด้วยตัวเลข "แก้" จะบวกหนึ่งโดยไม่มีของจริงรองรับ.
 */
export function diffBundles(leftParsed, rightParsed) {
  if (!leftParsed || !leftParsed.ok) return { ok: false, error: 'ไฟล์ฝั่งซ้ายยังใช้ไม่ได้' };
  if (!rightParsed || !rightParsed.ok) return { ok: false, error: 'ไฟล์ฝั่งขวายังใช้ไม่ได้' };
  if (leftParsed.kind !== rightParsed.kind) {
    return {
      ok: false,
      error: `เทียบข้ามชนิดไม่ได้ — ฝั่งซ้ายเป็น${KIND_LABEL[leftParsed.kind]} แต่ฝั่งขวาเป็น${KIND_LABEL[rightParsed.kind]}`,
    };
  }

  const kind = leftParsed.kind;
  const left = leftParsed.bundle;
  const right = rightParsed.bundle;

  const diff = kind === 'calc'
    ? diffCalcSnapshots(left, right)
    : diffQuestionSnapshots(left, right);

  const groups = kind === 'calc'
    ? [{ name: 'functions', title: 'สูตรคำนวณ', testid: 'tbc-group-functions', rows: decorate(diff.functions) }]
    : [
      { name: 'steps', title: 'Sections', testid: 'tbc-group-steps', rows: decorate(diff.steps) },
      { name: 'items', title: 'Questions', testid: 'tbc-group-items', rows: decorate(diff.items) },
    ];

  const header = headerRows(getProp(left, 'source'), getProp(right, 'source'));
  if (header.length) {
    groups.unshift({ name: 'header', title: 'หัวชุด (header)', testid: 'tbc-group-header', rows: header });
  }

  return {
    ok: true,
    kind,
    kindLabel: KIND_LABEL[kind] || kind,
    summary: diff.summary,
    groups,
  };
}

/**
 * `exportedAt` ของสองฝั่งบอกได้ไหมว่าใครเก่ากว่า — ใช้เสนอให้ผู้ใช้สลับข้าง (ไม่สลับให้เอง:
 * ผู้ใช้อาจตั้งใจวางฝั่งไหนก็ได้ และ `exportedAt` คือเวลาที่กด export ไม่ใช่เวลาที่แก้ข้อมูล).
 * → true เมื่อรู้แน่ว่าฝั่งขวา export ก่อนฝั่งซ้าย.
 */
export function rightLooksOlder(leftHeader, rightHeader) {
  const l = Date.parse((leftHeader && leftHeader.exportedAt) || '');
  const r = Date.parse((rightHeader && rightHeader.exportedAt) || '');
  if (!Number.isFinite(l) || !Number.isFinite(r)) return false;
  return r < l;
}
